import Chip from "@mui/material/Chip";
import withStyles from "@mui/styles/withStyles";
import {
  APP_ACP,
  SEARCH_TYPE_HOSPITAL,
  SEARCH_TYPE_TRANSPORT,
  TRACK_EVENTS,
} from "@recare/core/consts";
import { getReasonTranslation } from "@recare/core/model/auctions";
import { formatUnixDate } from "@recare/core/model/utils/dates";
import { useGetOntology } from "@recare/core/model/utils/ontologies/hooks";
import {
  Careprovider,
  GetOntologyType,
  NoCapacityInfo,
  NoCapacityReason,
  SearchType,
} from "@recare/core/types";
import {
  composeValidation,
  convertIn,
  convertModelDefinition,
  convertOut,
  FormElement,
  FormElementProps,
  FormRenderProps,
  FormWatcher,
  validateModel,
  valueDef,
} from "@recare/react-forms-state";
import { useLocale, useTranslations } from "@recare/translations";
import Translations from "@recare/translations/types";
import { useUpdateCareprovider } from "apollo/hooks/mutations";
import { useInvalidateCache } from "apollo/utils";
import { useEnvContext } from "context/EnvContext";
import {
  addDays,
  fromUnixTime,
  getHours,
  getMinutes,
  getUnixTime,
  isBefore,
  isToday,
  setHours,
  setMinutes,
  startOfDay,
} from "date-fns";
import { zonedTimeToUtc } from "date-fns-tz";
import DatePickerInputField from "ds/components/DatePickerInputField/lazy";
import RoundCard from "ds/components/RoundCard";
import RSButtonSaveProgress from "ds/components/RSButton/RSButtonSaveProgress";
import TimePicker from "ds/components/TimePicker";
import ToggleInputField from "ds/components/ToggleInputField";
import {
  APP_BACKGROUND,
  MEDIUM_GREY,
  TEXT_DARK_PRIMARY,
} from "ds/materials/colors";
import { margin, padding, sizing } from "ds/materials/metrics";
import { Body, Caption } from "ds/materials/typography";
import { useMedia } from "dsl/atoms/ResponsiveMedia";
import { useOnEnter } from "dsl/atoms/Routes";
import {
  FieldContainer,
  HintWrapper,
} from "dsl/ecosystems/CareproviderSettings";
import {
  filterToValidNoCapacities,
  hasNoCapacity,
} from "dsl/ecosystems/CareproviderSettings/shared";
import { useNotification } from "dsl/organisms/NotificationProvider";
import { Map } from "immutable";
import React, { useCallback, useReducer } from "react";
import { useTracking } from "react-tracking";
import styled from "styled-components";

type HasCapacityWithExceptionsReasonsProps = {
  getOntology: GetOntologyType;
  noCapacityExceptions: Careprovider["no_capacities"];
  onChange: Function;
  onDelete: (reason: NoCapacityInfo, idx: number) => void;
  translations: Translations;
};

const NoYes = styled(Caption)`
  display: flex;
  text-transform: uppercase;
  justify-content: flex-end;
  margin: ${margin(1, 0)};
`;

const ContentContainer = styled.div<{ isAcp: boolean }>`
  display: flex;
  flex-direction: column;
  background-color: ${APP_BACKGROUND};
  padding: ${({ isAcp }) => padding(1, isAcp ? 0 : 2, 1, 5)};
  margin: ${({ isAcp }) => margin(0, isAcp ? -3 : -5)};
`;

const StyledChip = withStyles({
  root: {
    minHeight: sizing(4),
    margin: margin(0.5, 5, 0.5, 0),
    color: TEXT_DARK_PRIMARY,
    justifyContent: "space-between",
  },
})(Chip);

const ButtonWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: ${({ isMobile }) => (isMobile ? "center" : "flex-end")};
  margin: ${({ isMobile }) =>
    isMobile ? margin(2, 0, 1, 0) : margin(4, 2, 3, 0)};
`;

export function shouldHandleTime({
  searchType,
}: {
  searchType: SearchType | undefined;
}): boolean {
  if (
    searchType === SEARCH_TYPE_HOSPITAL ||
    searchType === SEARCH_TYPE_TRANSPORT
  ) {
    return true;
  }
  return false;
}

export function appendTimeToCapacityDate({
  capacityDate,
  capacityTime,
}: {
  capacityDate: number;
  capacityTime: Date;
}): number {
  const start = startOfDay(fromUnixTime(capacityDate));
  return getUnixTime(
    setHours(
      setMinutes(start, getMinutes(capacityTime)),
      getHours(capacityTime),
    ),
  );
}

export function setTimeToMidnight(capacityDate: number) {
  return getUnixTime(
    zonedTimeToUtc(startOfDay(fromUnixTime(capacityDate)), "UTC"),
  );
}

export function getCapacityModel(location: "request" | "settings") {
  const isSettings = location === "settings";

  return {
    ...valueDef(isSettings ? "available_date" : "capacity_date", {
      convertOut: (
        value: number,
        props: AnyObject,
        modelValues: Map<string, Date>,
      ) => {
        const capacityTime = modelValues.get("_capacity_time");

        // when declining a request, selecting date & time is optional
        if (!isSettings && !value && !capacityTime) return null;

        const searchType = isSettings
          ? props?.formInputValue.patient_type
          : props?.auctionRequest?.auction?.search_type;

        if (capacityTime && shouldHandleTime({ searchType })) {
          return appendTimeToCapacityDate({
            capacityDate: value,
            capacityTime,
          });
        }
        // when time was not added by user, we set it to midnight
        return setTimeToMidnight(value);
      },
    }),
    ...valueDef("_capacity_time", {
      defaultValue: null,
      convertIn: (value: any, props: AnyObject) => {
        if (isSettings) {
          const capacityDate =
            props?.formInputValue?.profile?.capacity?.available_date;
          if (capacityDate) {
            return fromUnixTime(capacityDate);
          }
          return null;
        }
        return null;
      },
      convertOut: () => null,
    }),
  };
}

const capacityModel = getCapacityModel("settings");

export function getNoCapacityReasonsMessage({
  translations,
  getOntology,
  reasons = [],
}: {
  getOntology: GetOntologyType;
  reasons: Array<NoCapacityReason> | undefined;
  translations: Translations;
}): string {
  return Array.isArray(reasons)
    ? reasons
        .map(
          (reason) =>
            Array.isArray(reason.values) &&
            reason.values
              .map((reasonValue: number) =>
                getReasonTranslation({
                  reason: reason.type,
                  value: reasonValue,
                  translations,
                  getOntology,
                }),
              )
              .join(", "),
        )
        .join(", ")
    : "";
}

export function capacityFormReducer(state: AnyObject, action: AnyObject) {
  if (action.type === "UPDATE_NO_CAPACITIES") {
    return {
      careprovider: { ...state.careprovider, no_capacities: action.payload },
    };
  }
  return state;
}

const modelDefinition = convertModelDefinition({
  ...valueDef("no_capacities"),
  profile: {
    capacity: {
      ...valueDef("has_capacity", {
        defaultValue: true,
      }),
      ...capacityModel,
    },
  },
});

function HasCapacityWithExceptionsReasons({
  getOntology,
  noCapacityExceptions,
  onChange,
  onDelete,
  translations,
}: HasCapacityWithExceptionsReasonsProps) {
  const locale = useLocale();
  return noCapacityExceptions && noCapacityExceptions.length > 0 ? (
    <div data-testid="capacity-with-exceptions-container">
      <Body style={{ margin: margin(0, 0, 3, 0) }} color={MEDIUM_GREY}>
        {translations.careproviderApp.settings.capacity.exceptions}
      </Body>
      {noCapacityExceptions.map(
        (noCapacity: NoCapacityInfo, noCapacityIndex: number) =>
          Array.isArray(noCapacity.no_capacity_reasons) && (
            <StyledChip
              key={noCapacity.id}
              label={translations.careproviderProfile.capacity.noCapacityForGivenReasonUntil(
                {
                  reasons: getNoCapacityReasonsMessage({
                    translations,
                    getOntology,
                    reasons: noCapacity.no_capacity_reasons,
                  }),
                  date: formatUnixDate(noCapacity.expiration, locale),
                },
              )}
              onDelete={() => {
                const newNoCapacityExceptions = onDelete(
                  noCapacity,
                  noCapacityIndex,
                );
                onChange(newNoCapacityExceptions);
              }}
            />
          ),
      )}
    </div>
  ) : null;
}

const HasCapacityWithExceptionsReasonsConnected = FormElement()<
  FormElementProps & HasCapacityWithExceptionsReasonsProps
>(HasCapacityWithExceptionsReasons);

function NoCapacity({
  showTimePicker,
  translations,
}: {
  showTimePicker: boolean;
  translations: Translations;
}) {
  const TODAY_MIDNIGHT = getUnixTime(startOfDay(addDays(new Date(), 1)));

  return (
    <>
      <Body style={{ margin: margin(0, 0, 3, 0) }} color={MEDIUM_GREY}>
        {translations.careproviderApp.settings.capacity.acceptNewPatientsWhen}
      </Body>
      <DatePickerInputField
        blockBefore={showTimePicker ? undefined : TODAY_MIDNIGHT}
        disablePast
        elementName="available_date"
        label={translations.careproviderProfile.capacity.declineExpirationDate}
        required
      />
      {showTimePicker && (
        <TimePicker
          elementName="_capacity_time"
          hasCustomValidation
          label={translations.auctionRequest.capacityTime}
          marginOverride={margin(1, 2)}
          required
          width={sizing(21)}
        />
      )}
    </>
  );
}

function CapacityToggle({ translations }: { translations: Translations }) {
  return (
    <FieldContainer justify="space-between" alignItems="center">
      <Body margin={margin(0)}>
        {translations.careproviderApp.settings.capacity.capacityAvailable}
      </Body>
      <ToggleInputField elementName="has_capacity" />
    </FieldContainer>
  );
}

function Wrapper({
  children,
  isAcp,
  translations,
}: {
  children: React.ReactNode;
  isAcp: boolean;
  translations: Translations;
}) {
  if (isAcp) {
    return <>{children}</>;
  }
  return (
    <RoundCard
      nonRound
      childrenMargin={margin(2, 5)}
      title={translations.careproviderApp.settings.capacity.capacityTitle}
      breakpointMobile
    >
      {children}
    </RoundCard>
  );
}

export function validateAvailableDate(
  value: any,
  props: { formInputValue: Careprovider; translations: Translations },
) {
  if (
    shouldHandleTime({
      searchType: props.formInputValue.patient_type,
    })
  ) {
    if (!value.has_capacity && !value.available_date && !value._capacity_time) {
      return {
        available_date: props.translations.general.required,
        _capacity_time: props.translations.general.required,
      };
    }
    if (value.available_date && !value._capacity_time) {
      return { _capacity_time: props.translations.general.required };
    }
    if (value._capacity_time && !value.available_date) {
      return { available_date: props.translations.general.required };
    }
    if (
      isToday(value.available_date) &&
      isBefore(fromUnixTime(value._capacity_time), new Date())
    ) {
      return {
        _capacity_time: {
          customMessage:
            props.translations.careproviderApp.settings.capacity.dateInFuture,
        },
      };
    }
  } else {
    if (!value.has_capacity && !value.available_date) {
      return { available_date: props.translations.general.required };
    }
  }
  return true;
}

export default function CareproviderSettingsCapacity({
  careprovider,
}: {
  careprovider: Careprovider;
}) {
  useOnEnter();
  const { trackEvent } = useTracking();
  const notify = useNotification();
  const translations = useTranslations();
  const { app } = useEnvContext();
  const getOntology = useGetOntology();
  const { isMobile } = useMedia();
  const invalidateCache = useInvalidateCache();
  const isAcp = app === APP_ACP;

  const [state, dispatch] = useReducer(capacityFormReducer, {
    careprovider,
  });

  const noCapacityExceptions = hasNoCapacity(
    state.careprovider.profile?.capacity,
  )
    ? []
    : filterToValidNoCapacities(state.careprovider.no_capacities);

  const onCapacityExceptionRemove = useCallback(
    (noCapacityInfo: NoCapacityInfo, noCapacityIndex: number) => {
      const newNoCapacities = [...noCapacityExceptions];
      newNoCapacities.splice(noCapacityIndex, 1);
      dispatch({ type: "UPDATE_NO_CAPACITIES", payload: newNoCapacities });
      return newNoCapacities;
    },
    [noCapacityExceptions],
  );

  const [updateCareprovider, queryProgress, resetProgress] =
    useUpdateCareprovider({
      id: careprovider.id,
      onCompleted() {
        setTimeout(() => resetProgress(), 3000);
        trackEvent({
          name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_SAVED_CHANGES,
        });
        invalidateCache([`QUERY.careproviderRequestsNew`]);
        notify({
          type: "success",
          message: translations.general.success,
        });
      },
      onError(err) {
        console.error(JSON.stringify(err));
        notify({ type: "error", message: translations.general.error });
      },
    });

  const showTimePicker = shouldHandleTime({
    searchType: careprovider?.patient_type,
  });

  return (
    <FormRenderProps
      formInputValue={careprovider}
      convertIn={convertIn(modelDefinition)}
      convertOut={(value: AnyObject, props: AnyObject) => {
        const converted = convertOut(modelDefinition)(value, props);
        if (converted.profile.capacity.has_capacity) {
          converted.profile.capacity.available_date = null;
        } else {
          converted.no_capacities = null;
        }
        return converted;
      }}
      modelDefinition={modelDefinition}
      onSubmit={updateCareprovider}
      validate={composeValidation(
        validateModel(modelDefinition),
        validateAvailableDate,
      )}
    >
      {({ submit }) => (
        <Wrapper isAcp={isAcp} translations={translations}>
          <NoYes>
            {translations.careproviderApp.settings.capacity.toggleNoYes}
          </NoYes>

          <ContentContainer isAcp={isAcp}>
            <FormWatcher watchPath="has_capacity">
              {({ watchedValue: hasCapacity }: { watchedValue: boolean }) => {
                if (hasCapacity) {
                  return (
                    <>
                      <FormWatcher watchPath="no_capacities">
                        {({ watchedValue }) =>
                          isAcp ? (
                            <CapacityToggle translations={translations} />
                          ) : (
                            <HintWrapper
                              caption={
                                watchedValue?.length > 0
                                  ? translations.careproviderApp.settings
                                      .capacity.hintCapacityAvailableExcept
                                  : translations.careproviderApp.settings
                                      .capacity.hintCapacityAvailable
                              }
                            >
                              <CapacityToggle translations={translations} />
                            </HintWrapper>
                          )
                        }
                      </FormWatcher>
                      <HasCapacityWithExceptionsReasonsConnected
                        elementName="no_capacities"
                        translations={translations}
                        getOntology={getOntology}
                        noCapacityExceptions={noCapacityExceptions}
                        onDelete={onCapacityExceptionRemove}
                      />
                    </>
                  );
                } else {
                  return (
                    <>
                      {isAcp ? (
                        <CapacityToggle translations={translations} />
                      ) : (
                        <HintWrapper
                          caption={
                            showTimePicker
                              ? translations.auctionRequest
                                  .automaticDeclineWithTime
                              : translations.auctionRequest
                                  .automaticDeclineWithoutTime
                          }
                        >
                          <CapacityToggle translations={translations} />
                        </HintWrapper>
                      )}
                      <NoCapacity
                        showTimePicker={showTimePicker}
                        translations={translations}
                      />
                    </>
                  );
                }
              }}
            </FormWatcher>
          </ContentContainer>
          <ButtonWrapper
            isMobile={isMobile}
            data-testid="save-capacity-wrapper"
          >
            <RSButtonSaveProgress
              queryProgress={queryProgress}
              onSubmit={submit}
              translations={translations}
              style={{
                width: isMobile ? sizing(36) : sizing(19),
                margin: margin(0),
              }}
            />
          </ButtonWrapper>
        </Wrapper>
      )}
    </FormRenderProps>
  );
}
